import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const UserDashboard = () => {
    const { user, requestAdminRole, refreshUserData } = useUser();
    const [stats, setStats] = useState({
        totalBookings: 0,
        upcomingTrips: 0,
        wishlistItems: 0,
        completedTrips: 0
    });
    const [recentBookings, setRecentBookings] = useState([]);
    const [loading, setLoading] = useState(true);
    const [adminLoading, setAdminLoading] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // ✅ NEW: Manual refresh function
    const handleManualRefresh = async () => {
        setRefreshing(true);
        try {
            const result = await refreshUserData();
            if (result.success) {
                setMessage({
                    type: 'success',
                    text: 'Profile data refreshed successfully!'
                });
            } else {
                setMessage({
                    type: 'error',
                    text: result.message || 'Failed to refresh data'
                });
            }
        } catch (error) {
            setMessage({
                type: 'error',
                text: 'Error refreshing data: ' + error.message
            });
        } finally {
            setRefreshing(false);
        }
    };

    // ✅ UPDATE: Auto-refresh effect
    useEffect(() => {
        if (user) {
            loadUserData();

            // Auto-refresh every 2 minutes
            const interval = setInterval(async () => {
                console.log('🔄 Auto-refreshing user dashboard...');
                await refreshUserData();
            }, 120000);

            return () => clearInterval(interval);
        }
    }, [user, refreshUserData]);

    useEffect(() => {
        if (user) {
            loadUserData();
        }
    }, [user]);

    const loadUserData = async () => {
        try {
            // Simulate loading user data
            setStats({
                totalBookings: 3,
                upcomingTrips: 1,
                wishlistItems: 5,
                completedTrips: 2
            });

            setRecentBookings([
                {
                    id: 1,
                    packageName: "Sundarban Adventure",
                    date: "2024-02-15",
                    status: "Confirmed",
                    amount: 12500,
                    persons: 2,
                    duration: "3 days • 2 nights"
                },
                {
                    id: 2,
                    packageName: "Cox's Bazar Beach Tour",
                    date: "2024-01-20",
                    status: "Completed",
                    amount: 18500,
                    persons: 4,
                    duration: "4 days • 3 nights"
                }
            ]);
        } catch (error) {
            console.error('Error loading user data:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleAdminRequest = async () => {
        setAdminLoading(true);
        setMessage({ type: '', text: '' });

        try {
            console.log('🔄 Requesting admin access from dashboard...');
            const result = await requestAdminRole();

            if (result.success) {
                setMessage({
                    type: 'success',
                    text: '✅ Admin request submitted successfully! Please wait for approval from existing administrators.'
                });
                // Auto-refresh after 3 seconds to show updated status
                setTimeout(async () => {
                    await refreshUserData();
                }, 3000);
            } else {
                setMessage({
                    type: 'error',
                    text: `❌ ${result.message}`
                });
            }
        } catch (error) {
            console.error('Admin request error:', error);
            setMessage({
                type: 'error',
                text: `❌ Failed to submit admin request: ${error.message}`
            });
        } finally {
            setAdminLoading(false);
        }
    };

    const handleViewAdminPanel = () => {
        window.location.href = '/admin';
    };

    if (!user) {
        return (
            <div className="container-fluid py-5">
                <div className="container">
                    <div className="alert alert-warning text-center">
                        <h4>Please Login to Access Dashboard</h4>
                        <p>You need to be logged in to view your dashboard.</p>
                        <Link to="/" className="btn btn-primary">
                            Go to Homepage
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    if (loading) {
        return (
            <div className="container-fluid py-5">
                <div className="container">
                    <div className="text-center">
                        <div className="spinner-border text-primary" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <p className="mt-3">Loading your dashboard...</p>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4 bg-light min-vh-100">
            <div className="container">
                {/* Message Alert */}
                {message.text && (
                    <div className={`alert alert-dismissible fade show d-flex align-items-center mb-4 ${message.type === 'success' ? 'alert-success' : 'alert-danger'}`}>
                        <i className={`fas ${message.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'} me-2`}></i>
                        <div className="flex-grow-1">{message.text}</div>
                        <button
                            type="button"
                            className="btn-close"
                            onClick={() => setMessage({ type: '', text: '' })}
                        ></button>
                    </div>
                )}

                {/* Welcome Section with Admin Status */}
                <div className="row mb-5">
                    <div className="col-12">
                        <div className="card shadow-lg border-0 welcome-card bg-gradient-primary text-white">
                            <div className="card-body p-4 p-md-5">
                                <div className="row align-items-center">
                                    <div className="col-md-8">
                                        <div className="d-flex align-items-center">
                                            <div className="bg-white bg-opacity-20 p-3 rounded-3 me-4">
                                                <i className="fas fa-plane-departure fa-2x text-white"></i>
                                            </div>
                                            <div>
                                                <h1 className="h2 mb-1 fw-bold">
                                                    Welcome back, {user.name}! <span className="wave-emoji">👋</span>
                                                </h1>
                                                <p className="mb-0 opacity-75 fs-6">
                                                    Ready for your next adventure? Here's your travel overview.
                                                </p>
                                                <div className="mt-3">
                                                    <span className={`badge me-2 ${user.role === 'admin' ? 'bg-success' :
                                                        user.role === 'pending' ? 'bg-warning' : 'bg-info'
                                                        }`}>
                                                        <i className="fas fa-circle me-1" style={{ fontSize: '6px' }}></i>
                                                        {user.role === 'admin' ? 'Administrator' :
                                                            user.role === 'pending' ? 'Pending Admin Approval' : 'Traveler'}
                                                    </span>

                                                    {/* Refresh Button */}
                                                    <button
                                                        onClick={handleManualRefresh}
                                                        disabled={refreshing}
                                                        className="btn btn-outline-light btn-sm ms-2"
                                                        title="Refresh Data"
                                                    >
                                                        {refreshing ? (
                                                            <span className="spinner-border spinner-border-sm me-1"></span>
                                                        ) : (
                                                            <i className="fas fa-sync-alt me-1"></i>
                                                        )}
                                                        Refresh
                                                    </button>

                                                    {/* Admin Request Button for Regular Users */}
                                                    {user.role === 'user' && (
                                                        <button
                                                            onClick={handleAdminRequest}
                                                            disabled={adminLoading}
                                                            className="btn btn-light btn-sm text-primary fw-bold ms-2"
                                                        >
                                                            {adminLoading ? (
                                                                <>
                                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                                    Submitting...
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <i className="fas fa-user-shield me-1"></i>
                                                                    Request Admin Access
                                                                </>
                                                            )}
                                                        </button>
                                                    )}

                                                    {/* Admin Panel Button for Admins */}
                                                    {user.role === 'admin' && (
                                                        <button
                                                            onClick={handleViewAdminPanel}
                                                            className="btn btn-success btn-sm fw-bold ms-2"
                                                        >
                                                            <i className="fas fa-tachometer-alt me-1"></i>
                                                            Go to Admin Panel
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4 text-md-end">
                                        <div className="d-flex align-items-center justify-content-md-end">
                                            <div className="me-3 text-end d-none d-sm-block text-white">
                                                <div className="fw-bold fs-5">{user.name}</div>
                                                <small className="opacity-75">{user.email}</small>
                                                <div className="mt-1">
                                                    <small className="opacity-75">
                                                        Member since {user.createdAt ? new Date(user.createdAt).getFullYear() : '2024'}
                                                    </small>
                                                </div>
                                            </div>
                                            <div className="position-relative">
                                                <div className="bg-white text-primary rounded-circle d-flex align-items-center justify-content-center user-avatar"
                                                    style={{ width: '80px', height: '80px', fontSize: '28px', fontWeight: 'bold' }}>
                                                    {user.name.charAt(0).toUpperCase()}
                                                </div>
                                                <span className="position-absolute bottom-0 end-0 bg-success border border-3 border-white rounded-circle"
                                                    style={{ width: '16px', height: '16px' }}></span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Enhanced Stats Cards */}
                <div className="row mb-5">
                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card stats-card border-0 shadow-sm h-100 stats-card-primary">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <div className="text-muted small fw-semibold text-uppercase mb-1">
                                            Total Bookings
                                        </div>
                                        <div className="h3 fw-bold text-dark mb-2">
                                            {stats.totalBookings}
                                        </div>
                                        <div className="small text-muted">
                                            <i className="fas fa-trending-up text-success me-1"></i>
                                            +12% from last month
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="bg-primary bg-opacity-10 rounded-3 p-3">
                                            <i className="fas fa-calendar-check fa-2x text-primary"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card stats-card border-0 shadow-sm h-100 stats-card-success">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <div className="text-muted small fw-semibold text-uppercase mb-1">
                                            Upcoming Trips
                                        </div>
                                        <div className="h3 fw-bold text-dark mb-2">
                                            {stats.upcomingTrips}
                                        </div>
                                        <div className="small text-muted">
                                            Next trip in 15 days
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="bg-success bg-opacity-10 rounded-3 p-3">
                                            <i className="fas fa-suitcase fa-2x text-success"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card stats-card border-0 shadow-sm h-100 stats-card-info">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <div className="text-muted small fw-semibold text-uppercase mb-1">
                                            Wishlist Items
                                        </div>
                                        <div className="h3 fw-bold text-dark mb-2">
                                            {stats.wishlistItems}
                                        </div>
                                        <div className="small text-muted">
                                            Dream destinations saved
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="bg-info bg-opacity-10 rounded-3 p-3">
                                            <i className="fas fa-heart fa-2x text-info"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="col-xl-3 col-md-6 mb-4">
                        <div className="card stats-card border-0 shadow-sm h-100 stats-card-warning">
                            <div className="card-body p-4">
                                <div className="d-flex align-items-center">
                                    <div className="flex-grow-1">
                                        <div className="text-muted small fw-semibold text-uppercase mb-1">
                                            Completed Trips
                                        </div>
                                        <div className="h3 fw-bold text-dark mb-2">
                                            {stats.completedTrips}
                                        </div>
                                        <div className="small text-muted">
                                            Amazing memories made
                                        </div>
                                    </div>
                                    <div className="flex-shrink-0">
                                        <div className="bg-warning bg-opacity-10 rounded-3 p-3">
                                            <i className="fas fa-flag-checkered fa-2x text-warning"></i>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="row">
                    {/* Recent Bookings - Enhanced */}
                    <div className="col-lg-8 mb-4">
                        <div className="card shadow-sm border-0 h-100">
                            <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '44px', height: '44px' }}>
                                            <i className="fas fa-history text-primary"></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0 fw-bold text-dark">Recent Bookings</h5>
                                            <p className="text-muted mb-0 small">Your latest travel plans</p>
                                        </div>
                                    </div>
                                    <Link to="/bookings" className="btn btn-primary btn-sm px-3">
                                        <i className="fas fa-list me-2"></i>
                                        View All
                                    </Link>
                                </div>
                            </div>
                            <div className="card-body">
                                {recentBookings.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="bg-light rounded-circle d-inline-flex align-items-center justify-content-center mb-4"
                                            style={{ width: '100px', height: '100px' }}>
                                            <i className="fas fa-calendar-times fa-2x text-muted"></i>
                                        </div>
                                        <h5 className="fw-bold text-dark mb-3">No Bookings Yet</h5>
                                        <p className="text-muted mb-4">Start your journey by booking an amazing trip!</p>
                                        <Link to="/packages" className="btn btn-primary px-4">
                                            <i className="fas fa-search me-2"></i>
                                            Browse Packages
                                        </Link>
                                    </div>
                                ) : (
                                    <div className="table-responsive">
                                        <table className="table table-hover align-middle">
                                            <thead className="bg-light">
                                                <tr>
                                                    <th className="border-0 fw-semibold text-muted">Package</th>
                                                    <th className="border-0 fw-semibold text-muted">Date</th>
                                                    <th className="border-0 fw-semibold text-muted">Travelers</th>
                                                    <th className="border-0 fw-semibold text-muted">Amount</th>
                                                    <th className="border-0 fw-semibold text-muted">Status</th>
                                                    <th className="border-0 fw-semibold text-muted">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {recentBookings.map(booking => (
                                                    <tr key={booking.id} className="booking-row">
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <div className="bg-primary bg-opacity-10 rounded-2 p-2 me-3">
                                                                    <i className="fas fa-map-marked-alt text-primary"></i>
                                                                </div>
                                                                <div>
                                                                    <strong className="text-dark d-block">{booking.packageName}</strong>
                                                                    <small className="text-muted">{booking.duration}</small>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="text-dark fw-medium">
                                                                {new Date(booking.date).toLocaleDateString()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex align-items-center">
                                                                <i className="fas fa-users text-muted me-2"></i>
                                                                <span className="text-dark">{booking.persons} Persons</span>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <div className="fw-bold text-dark">
                                                                ৳{booking.amount.toLocaleString()}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`badge badge-status ${booking.status === 'Confirmed' ? 'bg-success' :
                                                                booking.status === 'Pending' ? 'bg-warning' :
                                                                    booking.status === 'Cancelled' ? 'bg-danger' : 'bg-secondary'
                                                                }`}>
                                                                <i className={`fas ${booking.status === 'Confirmed' ? 'fa-check' :
                                                                    booking.status === 'Pending' ? 'fa-clock' :
                                                                        booking.status === 'Cancelled' ? 'fa-times' : 'fa-info-circle'
                                                                    } me-1`}></i>
                                                                {booking.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <button className="btn btn-outline-primary btn-sm">
                                                                <i className="fas fa-eye me-1"></i>
                                                                Details
                                                            </button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Sidebar Section with Admin Features */}
                    <div className="col-lg-4">
                        {/* Quick Actions */}
                        <div className="card shadow-sm border-0 mb-4">
                            <div className="card-header bg-transparent border-0 pt-4 pb-3">
                                <div className="d-flex align-items-center">
                                    <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                                        style={{ width: '44px', height: '44px' }}>
                                        <i className="fas fa-bolt text-primary"></i>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0 fw-bold text-dark">Quick Actions</h5>
                                        <p className="text-muted mb-0 small">Fast access to features</p>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="d-grid gap-3">
                                    <Link to="/packages" className="btn btn-primary btn-lg py-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="fas fa-search me-3 fs-5"></i>
                                            <div className="text-start">
                                                <div className="fw-semibold">Browse Packages</div>
                                                <small className="opacity-75">Discover new destinations</small>
                                            </div>
                                        </div>
                                    </Link>

                                    <Link to="/user-profile" className="btn btn-outline-primary py-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="fas fa-user-edit me-3 fs-5"></i>
                                            <div className="text-start">
                                                <div className="fw-semibold">Edit Profile</div>
                                                <small className="opacity-75">Update your information</small>
                                            </div>
                                        </div>
                                    </Link>

                                    {/* Admin Request Button for Regular Users */}
                                    {user.role === 'user' && (
                                        <button
                                            onClick={handleAdminRequest}
                                            disabled={adminLoading}
                                            className="btn btn-outline-warning py-3"
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-user-shield me-3 fs-5"></i>
                                                <div className="text-start">
                                                    <div className="fw-semibold">
                                                        {adminLoading ? 'Submitting...' : 'Request Admin Access'}
                                                    </div>
                                                    <small className="opacity-75">Get administrator privileges</small>
                                                </div>
                                            </div>
                                        </button>
                                    )}

                                    {/* Admin Panel Link for Admins */}
                                    {user.role === 'admin' && (
                                        <button
                                            onClick={handleViewAdminPanel}
                                            className="btn btn-success py-3"
                                        >
                                            <div className="d-flex align-items-center justify-content-center">
                                                <i className="fas fa-tachometer-alt me-3 fs-5"></i>
                                                <div className="text-start">
                                                    <div className="fw-semibold">Admin Panel</div>
                                                    <small className="opacity-75">Manage the system</small>
                                                </div>
                                            </div>
                                        </button>
                                    )}

                                    {/* Pending Approval Message */}
                                    {user.role === 'pending' && (
                                        <div className="alert alert-warning py-3">
                                            <div className="d-flex align-items-center">
                                                <i className="fas fa-clock me-3 fs-5 text-warning"></i>
                                                <div className="text-start">
                                                    <div className="fw-semibold">Pending Approval</div>
                                                    <small className="opacity-75">Admin request under review</small>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    <button className="btn btn-outline-info py-3">
                                        <div className="d-flex align-items-center justify-content-center">
                                            <i className="fas fa-question-circle me-3 fs-5"></i>
                                            <div className="text-start">
                                                <div className="fw-semibold">Get Help</div>
                                                <small className="opacity-75">24/7 customer support</small>
                                            </div>
                                        </div>
                                    </button>
                                </div>
                            </div>
                        </div>

                        {/* Admin Status Card */}
                        <div className={`card shadow-sm border-0 ${user.role === 'admin' ? 'border-success' :
                            user.role === 'pending' ? 'border-warning' : 'border-info'
                            }`}>
                            <div className={`card-header bg-transparent ${user.role === 'admin' ? 'bg-success bg-opacity-10 text-success' :
                                user.role === 'pending' ? 'bg-warning bg-opacity-10 text-warning' : 'bg-info bg-opacity-10 text-info'
                                } border-bottom-0 pt-4 pb-3`}>
                                <div className="d-flex align-items-center">
                                    <div className={`rounded-circle d-flex align-items-center justify-content-center me-3 ${user.role === 'admin' ? 'bg-success text-white' :
                                        user.role === 'pending' ? 'bg-warning text-white' : 'bg-info text-white'
                                        }`}
                                        style={{ width: '44px', height: '44px' }}>
                                        <i className={`fas ${user.role === 'admin' ? 'fa-user-shield' :
                                            user.role === 'pending' ? 'fa-clock' : 'fa-user'
                                            }`}></i>
                                    </div>
                                    <div>
                                        <h5 className="card-title mb-0 fw-bold">Account Status</h5>
                                        <p className="mb-0 small">Your current privileges</p>
                                    </div>
                                </div>
                            </div>
                            <div className="card-body text-center p-4">
                                <div className={`bg-${user.role === 'admin' ? 'success' :
                                    user.role === 'pending' ? 'warning' : 'info'
                                    } bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3`}
                                    style={{ width: '80px', height: '80px' }}>
                                    <i className={`fas fa-2x text-${user.role === 'admin' ? 'success' :
                                        user.role === 'pending' ? 'warning' : 'info'
                                        } ${user.role === 'admin' ? 'fa-user-shield' :
                                            user.role === 'pending' ? 'fa-clock' : 'fa-user'
                                        }`}></i>
                                </div>
                                <h6 className="fw-bold text-dark mb-2 text-capitalize">
                                    {user.role === 'admin' ? 'Administrator' :
                                        user.role === 'pending' ? 'Pending Admin Approval' : 'Regular User'}
                                </h6>

                                {user.role === 'user' && (
                                    <>
                                        <p className="text-muted small mb-3">
                                            You can request admin access to manage the system and access advanced features.
                                        </p>
                                        <button
                                            onClick={handleAdminRequest}
                                            disabled={adminLoading}
                                            className="btn btn-warning btn-sm"
                                        >
                                            {adminLoading ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm me-1"></span>
                                                    Submitting...
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane me-1"></i>
                                                    Request Access
                                                </>
                                            )}
                                        </button>
                                    </>
                                )}

                                {user.role === 'pending' && (
                                    <>
                                        <p className="text-muted small mb-3">
                                            Your admin request is under review. Please wait for approval from existing administrators.
                                        </p>
                                        <div className="alert alert-warning small mb-0">
                                            <i className="fas fa-info-circle me-1"></i>
                                            You'll be notified once approved
                                        </div>
                                    </>
                                )}

                                {user.role === 'admin' && (
                                    <>
                                        <p className="text-muted small mb-3">
                                            You have full administrator privileges. Access the admin panel to manage the system.
                                        </p>
                                        <button
                                            onClick={handleViewAdminPanel}
                                            className="btn btn-success btn-sm"
                                        >
                                            <i className="fas fa-tachometer-alt me-1"></i>
                                            Go to Admin Panel
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* Upcoming Trip */}
                        {stats.upcomingTrips > 0 && (
                            <div className="card shadow-sm border-warning upcoming-trip-card mt-4">
                                <div className="card-header bg-warning bg-opacity-10 border-warning border-bottom-0 pt-4 pb-3">
                                    <div className="d-flex align-items-center">
                                        <div className="bg-warning rounded-circle d-flex align-items-center justify-content-center me-3"
                                            style={{ width: '44px', height: '44px' }}>
                                            <i className="fas fa-plane text-white"></i>
                                        </div>
                                        <div>
                                            <h5 className="card-title mb-0 fw-bold text-dark">Next Trip</h5>
                                            <p className="text-muted mb-0 small">Your upcoming adventure</p>
                                        </div>
                                    </div>
                                </div>
                                <div className="card-body text-center p-4">
                                    <div className="bg-warning bg-opacity-10 rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                                        style={{ width: '80px', height: '80px' }}>
                                        <i className="fas fa-suitcase fa-2x text-warning"></i>
                                    </div>
                                    <h6 className="fw-bold text-dark mb-2">Sundarban Adventure</h6>
                                    <p className="text-muted small mb-3">Explore the magical mangrove forest</p>

                                    <div className="d-flex justify-content-center mb-3">
                                        <div className="text-center mx-3">
                                            <div className="fw-bold text-dark">15</div>
                                            <small className="text-muted">Days left</small>
                                        </div>
                                        <div className="text-center mx-3">
                                            <div className="fw-bold text-dark">Feb 15</div>
                                            <small className="text-muted">Departure</small>
                                        </div>
                                        <div className="text-center mx-3">
                                            <div className="fw-bold text-dark">4</div>
                                            <small className="text-muted">Travelers</small>
                                        </div>
                                    </div>

                                    <div className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-3 py-2 mb-3">
                                        <i className="fas fa-check-circle me-1"></i>
                                        Confirmed & Paid
                                    </div>

                                    <div className="mt-3">
                                        <button className="btn btn-warning btn-sm px-4 me-2">
                                            <i className="fas fa-eye me-1"></i>
                                            View Details
                                        </button>
                                        <button className="btn btn-outline-warning btn-sm px-3">
                                            <i className="fas fa-download me-1"></i>
                                            Invoice
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Custom CSS */}
            <style jsx>{`
                .bg-gradient-primary {
                    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
                }
                .stats-card-primary:hover {
                    transform: translateY(-5px);
                    transition: all 0.3s ease;
                }
                .stats-card-success:hover {
                    transform: translateY(-5px);
                    transition: all 0.3s ease;
                }
                .stats-card-info:hover {
                    transform: translateY(-5px);
                    transition: all 0.3s ease;
                }
                .stats-card-warning:hover {
                    transform: translateY(-5px);
                    transition: all 0.3s ease;
                }
                .user-avatar {
                    box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                }
                .booking-row:hover {
                    background-color: #f8f9fa;
                    transition: all 0.2s ease;
                }
            `}</style>
        </div>
    );
};

export default UserDashboard;